// ** react imports
import { useState, useMemo, useEffect } from 'react';

// ** api methods for mobile application version management
import {
  useCreateMobileAppVersionMutation,
  useGetAllMobileAppVersionsQuery,
  useGetActiveVersionQuery,
  useUpdateMobileAppVersionMutation,
  useDeleteMobileAppVersionMutation,
  useCheckVersionSupportQuery,
} from '../../../redux/slices/versions/slice';

export const useCreateMobileAppVersion = () => {
  try {
    // api call mutation
    const [
      createMobileAppVersion,
      {
        isLoading: isCreateMobileAppVersionLoading,
        isError: isCreateMobileAppVersionError,
        error: createMobileAppVersionError,
        isSuccess: isCreateMobileAppVersionSuccess,
        isFetching: isCreateMobileAppVersionFetching,
      },
    ] = useCreateMobileAppVersionMutation();

    // handlers
    const handleCreateMobileAppVersion = async data => {
      return await createMobileAppVersion(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isCreateMobileAppVersionLoading,
        isCreateMobileAppVersionFetching,
        isCreateMobileAppVersionError,
        createMobileAppVersionError,
        isCreateMobileAppVersionSuccess,
        createMobileAppVersion: handleCreateMobileAppVersion,
      }),
      [
        isCreateMobileAppVersionLoading,
        isCreateMobileAppVersionFetching,
        isCreateMobileAppVersionError,
        createMobileAppVersionError,
        isCreateMobileAppVersionSuccess,
        handleCreateMobileAppVersion,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error creating mobile app version:', error);
    throw error;
  }
};

export const useGetAllMobileAppVersions = () => {
  try {
    // states
    const [data, setData] = useState([]);
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getAllMobileAppVersions,
      isLoading: isGetAllMobileAppVersionsLoading,
      isError: isGetAllMobileAppVersionsError,
      error: getAllMobileAppVersionsError,
      isFetching: isGetAllMobileAppVersionsFetching,
      refetch: refetchGetAllMobileAppVersions,
    } = useGetAllMobileAppVersionsQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetAllMobileAppVersions = async (
      page,
      limit,
      platform,
      status,
      search
    ) => {
      setSkipApi(false);
      // Filter out null/undefined values from query before sending to API
      const cleanQuery = Object.fromEntries(
        Object.entries({ page, limit, platform, status, search }).filter(
          ([_, value]) => value !== null && value !== undefined && value !== ''
        )
      );
      setQuery(cleanQuery);
    };

    // effect
    useEffect(() => {
      if (getAllMobileAppVersions !== undefined) {
        setData(getAllMobileAppVersions.data);
        setSkipApi(true);
      }
    }, [getAllMobileAppVersions]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAllMobileAppVersionsLoading,
        isGetAllMobileAppVersionsError,
        getAllMobileAppVersionsError,
        isGetAllMobileAppVersionsFetching,
        refetchGetAllMobileAppVersions,
        getAllMobileAppVersions: handleGetAllMobileAppVersions,
      }),
      [
        data,
        isGetAllMobileAppVersionsLoading,
        isGetAllMobileAppVersionsError,
        getAllMobileAppVersionsError,
        isGetAllMobileAppVersionsFetching,
        refetchGetAllMobileAppVersions,
        handleGetAllMobileAppVersions,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching all mobile app versions:', error);
    throw error;
  }
};

export const useGetActiveVersion = () => {
  try {
    // states
    const [data, setData] = useState(null);
    const [skipApi, setSkipApi] = useState(true);
    const [platform, setPlatform] = useState('');

    // api call query
    const {
      data: getActiveVersion,
      isLoading: isGetActiveVersionLoading,
      isFetching: isGetActiveVersionFetching,
      isError: isGetActiveVersionError,
      error: getActiveVersionError,
      refetch: refetchGetActiveVersion,
    } = useGetActiveVersionQuery(platform, { skip: skipApi });

    // handlers
    const handleGetActiveVersion = async platform => {
      if (!platform) {
        throw new Error('Platform is required');
      }
      setSkipApi(false);
      setPlatform(platform);
    };

    // effect
    useEffect(() => {
      if (getActiveVersion !== undefined) {
        setData(getActiveVersion.data);
        setSkipApi(true);
      }
    }, [getActiveVersion]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetActiveVersionLoading,
        isGetActiveVersionFetching,
        isGetActiveVersionError,
        getActiveVersionError,
        refetchGetActiveVersion,
        getActiveVersion: handleGetActiveVersion,
      }),
      [
        data,
        isGetActiveVersionLoading,
        isGetActiveVersionFetching,
        isGetActiveVersionError,
        getActiveVersionError,
        refetchGetActiveVersion,
        handleGetActiveVersion,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching active version:', error);
    throw error;
  }
};

export const useUpdateMobileAppVersion = () => {
  try {
    // api call mutation
    const [
      updateMobileAppVersion,
      {
        isLoading: isUpdateMobileAppVersionLoading,
        isError: isUpdateMobileAppVersionError,
        error: updateMobileAppVersionError,
        isSuccess: isUpdateMobileAppVersionSuccess,
        isFetching: isUpdateMobileAppVersionFetching,
      },
    ] = useUpdateMobileAppVersionMutation();

    // handlers
    const handleUpdateMobileAppVersion = async (id, data) => {
      if (!id) {
        throw new Error('Version ID is required');
      }
      return await updateMobileAppVersion({ id, data });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUpdateMobileAppVersionLoading,
        isUpdateMobileAppVersionFetching,
        isUpdateMobileAppVersionError,
        updateMobileAppVersionError,
        isUpdateMobileAppVersionSuccess,
        updateMobileAppVersion: handleUpdateMobileAppVersion,
      }),
      [
        isUpdateMobileAppVersionLoading,
        isUpdateMobileAppVersionFetching,
        isUpdateMobileAppVersionError,
        updateMobileAppVersionError,
        isUpdateMobileAppVersionSuccess,
        handleUpdateMobileAppVersion,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error updating mobile app version:', error);
    throw error;
  }
};

export const useDeleteMobileAppVersion = () => {
  try {
    // api call mutation
    const [
      deleteMobileAppVersion,
      {
        isLoading: isDeleteMobileAppVersionLoading,
        isError: isDeleteMobileAppVersionError,
        error: deleteMobileAppVersionError,
        isSuccess: isDeleteMobileAppVersionSuccess,
        isFetching: isDeleteMobileAppVersionFetching,
      },
    ] = useDeleteMobileAppVersionMutation();

    // handlers
    const handleDeleteMobileAppVersion = async id => {
      if (!id) {
        throw new Error('Version ID is required');
      }
      return await deleteMobileAppVersion(id);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isDeleteMobileAppVersionLoading,
        isDeleteMobileAppVersionFetching,
        isDeleteMobileAppVersionError,
        deleteMobileAppVersionError,
        isDeleteMobileAppVersionSuccess,
        deleteMobileAppVersion: handleDeleteMobileAppVersion,
      }),
      [
        isDeleteMobileAppVersionLoading,
        isDeleteMobileAppVersionFetching,
        isDeleteMobileAppVersionError,
        deleteMobileAppVersionError,
        isDeleteMobileAppVersionSuccess,
        handleDeleteMobileAppVersion,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error deleting mobile app version:', error);
    throw error;
  }
};

export const useCheckVersionSupport = () => {
  try {
    // states
    const [data, setData] = useState(null);
    const [skipApi, setSkipApi] = useState(true);
    const [versionInfo, setVersionInfo] = useState({});

    // api call query
    const {
      data: checkVersionSupport,
      isLoading: isCheckVersionSupportLoading,
      isFetching: isCheckVersionSupportFetching,
      isError: isCheckVersionSupportError,
      error: checkVersionSupportError,
      refetch: refetchCheckVersionSupport,
    } = useCheckVersionSupportQuery(versionInfo, { skip: skipApi });

    // handlers
    const handleCheckVersionSupport = async (platform, version) => {
      if (!platform) {
        throw new Error('Platform is required');
      }
      if (!version) {
        throw new Error('Version is required');
      }
      setSkipApi(false);
      setVersionInfo({ platform, version });
    };

    // effect
    useEffect(() => {
      if (checkVersionSupport !== undefined) {
        setData(checkVersionSupport.data);
        setSkipApi(true);
      }
    }, [checkVersionSupport]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isCheckVersionSupportLoading,
        isCheckVersionSupportFetching,
        isCheckVersionSupportError,
        checkVersionSupportError,
        refetchCheckVersionSupport,
        checkVersionSupport: handleCheckVersionSupport,
      }),
      [
        data,
        isCheckVersionSupportLoading,
        isCheckVersionSupportFetching,
        isCheckVersionSupportError,
        checkVersionSupportError,
        refetchCheckVersionSupport,
        handleCheckVersionSupport,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error checking version support:', error);
    throw error;
  }
};
